import { User, UserRole, Project, ProjectMember, Agency } from '@/types'

/**
 * Permission utilities for multi-tenant SaaS
 *
 * Role Impersonation Support:
 * Many functions accept an optional `effectiveRole` parameter. When provided,
 * this role is used instead of the user's actual role for permission checks.
 * This allows super admins to see the app as other roles would see it.
 *
 * For UI visibility checks (what to show), pass effectiveRole from AuthContext.
 * For actual permission enforcement (what to allow), use the user's real role.
 */

// ====================================
// SUBSCRIPTION HELPERS
// ====================================

/**
 * Check if an agency's subscription is currently valid
 */
export const isSubscriptionValid = (agency: Agency | null): boolean => {
  if (!agency) return false

  // Check if subscription status is active or trialing
  const validStatuses: Agency['subscription_status'][] = ['active', 'trialing']
  if (!validStatuses.includes(agency.subscription_status)) {
    return false
  }

  // If no end date, subscription is unlimited (valid)
  if (!agency.subscription_end_date) {
    return true
  }

  // Check if end date is in the future
  const endDate = new Date(agency.subscription_end_date)
  return endDate > new Date()
}

/**
 * Get the effective project limit based on the agency's plan type
 */
export const getEffectiveProjectLimit = (agency: Agency | null): number | null => {
  if (!agency) return 0

  // Agency tier has unlimited projects
  if (agency.plan_type === 'agency') {
    return null // null = unlimited
  }

  // Pro tier has unlimited projects (pay per active project)
  if (agency.plan_type === 'pro') {
    return null // null = unlimited
  }

  // Free tier has 1 project
  if (agency.plan_type === 'free') {
    return 1
  }

  // Fallback to project_limit field
  return agency.project_limit ?? 1
}

/**
 * Get the number of remaining projects an agency can create
 * Returns null if unlimited
 */
export const getProjectsRemaining = (
  agency: Agency | null,
  projectCount: number
): number | null => {
  const limit = getEffectiveProjectLimit(agency)

  // null = unlimited
  if (limit === null) {
    return null
  }

  return Math.max(0, limit - projectCount)
}

// ====================================
// PROJECT PERMISSIONS
// ====================================

export const canCreateProject = (
  user: User | null,
  agency: Agency | null,
  projectCount: number
): boolean => {
  if (!user) return false

  // Super admins can always create projects, regardless of agency
  if (user.role === 'super_admin') return true

  // Admins need an agency
  if (!agency) return false

  // Admins can create projects for their agency
  if (user.role === 'admin' && user.agency_id === agency.id) {
    if (!isSubscriptionValid(agency)) return false
    const effectiveLimit = getEffectiveProjectLimit(agency)
    if (effectiveLimit === null) return true
    return projectCount < effectiveLimit
  }

  // Personal account owners (free-tier users) can create projects within limits
  if (agency.account_type === 'personal' && agency.owner_user_id === user.id) {
    if (!isSubscriptionValid(agency)) return false
    const effectiveLimit = getEffectiveProjectLimit(agency)
    if (effectiveLimit === null) return true
    return projectCount < effectiveLimit
  }

  return false
}

export const canViewProject = (
  user: User | null,
  project: Project | null,
  members: ProjectMember[] = []
): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  if (user.role === 'admin' && user.agency_id === project.agency_id) return true
  // Check if user is assigned to project
  return members.some((m) => m.user_id === user.id)
}

export const canEditProject = (
  user: User | null,
  project: Project | null,
  members: ProjectMember[] = []
): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  if (user.role === 'admin' && user.agency_id === project.agency_id) return true
  // Managers can edit if assigned
  if (user.role === 'manager') {
    return members.some((m) => m.user_id === user.id && m.role === 'manager')
  }
  // Clients can edit if assigned to the project (they provide the content!)
  if (user.role === 'client') {
    return members.some((m) => m.user_id === user.id)
  }
  return false
}

export const canDeleteProject = (
  user: User | null,
  project: Project | null
): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  return user.role === 'admin' && user.agency_id === project.agency_id
}

/**
 * Check if user can approve/reject section suggestions
 * Only admins and super_admins can approve suggestions
 */
export const canApproveSuggestion = (user: User | null, project: Project | null): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  return user.role === 'admin' && user.agency_id === project.agency_id
}

// ====================================
// USER/TEAM PERMISSIONS
// ====================================

export const canInviteUsers = (user: User | null): boolean => {
  if (!user) return false
  return user.role === 'super_admin' || user.role === 'admin'
}

export const canManageProjectMembers = (
  user: User | null,
  project: Project | null
): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  return user.role === 'admin' && user.agency_id === project.agency_id
}

export const canRemoveUser = (
  actor: User | null,
  targetUser: User | null
): boolean => {
  if (!actor || !targetUser) return false
  if (actor.role === 'super_admin') return true
  // Admins can remove users in their agency (except other admins)
  if (actor.role === 'admin' && actor.agency_id === targetUser.agency_id) {
    return targetUser.role !== 'admin' && targetUser.role !== 'super_admin'
  }
  return false
}

// ====================================
// AGENCY PERMISSIONS
// ====================================

export const canManageAgency = (user: User | null, agencyId: string): boolean => {
  if (!user) return false
  if (user.role === 'super_admin') return true
  return user.role === 'admin' && user.agency_id === agencyId
}

export const canCreateAgency = (user: User | null): boolean => {
  if (!user) return false
  return user.role === 'super_admin'
}

export const canDeleteAgency = (user: User | null): boolean => {
  if (!user) return false
  return user.role === 'super_admin'
}

export const canSetProjectLimit = (user: User | null): boolean => {
  if (!user) return false
  return user.role === 'super_admin'
}

// ====================================
// ROLE CHECKS
// ====================================

/**
 * Check if user is super admin
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const isSuperAdmin = (user: User | null, effectiveRole?: UserRole): boolean => {
  const role = effectiveRole ?? user?.role
  return role === 'super_admin'
}

/**
 * Check if user is admin
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const isAdmin = (user: User | null, effectiveRole?: UserRole): boolean => {
  const role = effectiveRole ?? user?.role
  return role === 'admin'
}

/**
 * Check if user is manager
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const isManager = (user: User | null, effectiveRole?: UserRole): boolean => {
  const role = effectiveRole ?? user?.role
  return role === 'manager'
}

/**
 * Check if user is client
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const isClient = (user: User | null, effectiveRole?: UserRole): boolean => {
  const role = effectiveRole ?? user?.role
  return role === 'client'
}

/**
 * Check if user has a specific role
 * @param user - The user to check
 * @param role - The role to check for
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const hasRole = (user: User | null, role: UserRole, effectiveRole?: UserRole): boolean => {
  const userRole = effectiveRole ?? user?.role
  return userRole === role
}

// ====================================
// ROLE HIERARCHY
// ====================================

const roleHierarchy = {
  super_admin: 4,
  admin: 3,
  manager: 2,
  client: 1,
}

/**
 * Check if user has a higher role than the compared role
 * @param user - The user to check
 * @param compareRole - The role to compare against
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const hasHigherRole = (
  user: User | null,
  compareRole: UserRole,
  effectiveRole?: UserRole
): boolean => {
  if (!user) return false
  const role = effectiveRole ?? user.role
  return roleHierarchy[role] > roleHierarchy[compareRole]
}

/**
 * Check if user has an equal or higher role than the compared role
 * @param user - The user to check
 * @param compareRole - The role to compare against
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const hasEqualOrHigherRole = (
  user: User | null,
  compareRole: UserRole,
  effectiveRole?: UserRole
): boolean => {
  if (!user) return false
  const role = effectiveRole ?? user.role
  return roleHierarchy[role] >= roleHierarchy[compareRole]
}

// ====================================
// UI HELPERS
// ====================================

export const getRoleBadgeColor = (role: User['role']): string => {
  switch (role) {
    case 'super_admin':
      return 'bg-amber-100 text-amber-700 border-amber-300'
    case 'admin':
      return 'bg-blue-100 text-blue-700 border-blue-300'
    case 'manager':
      return 'bg-green-100 text-green-700 border-green-300'
    case 'client':
      return 'bg-gray-100 text-gray-700 border-gray-300'
  }
}

export const getRoleDisplayName = (role: User['role']): string => {
  switch (role) {
    case 'super_admin':
      return 'Super Admin'
    case 'admin':
      return 'Agency Admin'
    case 'manager':
      return 'Project Manager'
    case 'client':
      return 'Client'
  }
}

export const getRoleBadge = (role: User['role']): string => {
  return getRoleDisplayName(role)
}

/**
 * Check if user can manage team members
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const canManageTeam = (user: User | null, effectiveRole?: UserRole): boolean => {
  if (!user) return false
  const role = effectiveRole ?? user.role
  return role === 'super_admin' || role === 'admin'
}

/**
 * Check if user has admin-level privileges (admin or super_admin)
 * Use this for features that should be available to both admins and super admins
 * Examples: export functionality, viewing admin-only UI elements
 * @param user - The user to check
 * @param effectiveRole - Optional role override for impersonation UI
 */
export const hasAdminPrivileges = (user: User | null, effectiveRole?: UserRole): boolean => {
  if (!user) return false
  const role = effectiveRole ?? user.role
  return role === 'admin' || role === 'super_admin'
}

// ====================================
// SUBSCRIPTION UI HELPERS
// ====================================

/**
 * Get display name for plan type
 */
export const getPlanDisplayName = (planType: Agency['plan_type']): string => {
  switch (planType) {
    case 'free':
      return 'Free'
    case 'pro':
      return 'Pro'
    case 'agency':
      return 'Agency'
    default:
      return planType
  }
}

/**
 * Get display name for subscription status
 */
export const getSubscriptionStatusDisplayName = (status: Agency['subscription_status']): string => {
  switch (status) {
    case 'active':
      return 'Actief'
    case 'expired':
      return 'Verlopen'
    case 'cancelled':
      return 'Geannuleerd'
    case 'trialing':
      return 'Proefperiode'
    default:
      return status
  }
}

/**
 * Get badge color for subscription status
 */
export const getSubscriptionStatusBadgeColor = (status: Agency['subscription_status']): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700 border-green-300'
    case 'trialing':
      return 'bg-blue-100 text-blue-700 border-blue-300'
    case 'expired':
      return 'bg-red-100 text-red-700 border-red-300'
    case 'cancelled':
      return 'bg-gray-100 text-gray-700 border-gray-300'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300'
  }
}

/**
 * Get badge color for plan type
 */
export const getPlanBadgeColor = (planType: Agency['plan_type']): string => {
  switch (planType) {
    case 'agency':
      return 'bg-yellow-100 text-yellow-700 border-yellow-300'
    case 'pro':
      return 'bg-gray-100 text-gray-700 border-gray-300'
    case 'free':
      return 'bg-amber-100 text-amber-700 border-amber-300'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300'
  }
}

/**
 * Format date for display
 */
export const formatSubscriptionDate = (dateString: string | null): string => {
  if (!dateString) return 'Onbeperkt'
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

/**
 * Get days remaining until subscription expires
 */
export const getDaysRemaining = (endDate: string | null): number | null => {
  if (!endDate) return null

  const end = new Date(endDate)
  const now = new Date()
  const diff = end.getTime() - now.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

// ====================================
// MODULE PERMISSIONS
// ====================================

/**
 * Check if user can add/review/publish agency-scoped knowledge documents.
 * Agency admins of the same agency, or super admins.
 */
export const canEditAgencyKnowledge = (
  user: User | null,
  agencyId: string | null | undefined,
): boolean => {
  if (!user) return false
  if (user.role === 'super_admin') return true
  if (!agencyId) return false
  return user.role === 'admin' && user.agency_id === agencyId
}

/**
 * Check if user can add/review/publish project-scoped knowledge documents.
 * Super admins, agency admins of the project's agency, or project managers
 * assigned to the project.
 */
export const canEditProjectKnowledge = (
  user: User | null,
  projectAgencyId: string | null | undefined,
  members: ProjectMember[] = [],
): boolean => {
  if (!user) return false
  if (user.role === 'super_admin') return true
  if (projectAgencyId && user.role === 'admin' && user.agency_id === projectAgencyId) {
    return true
  }
  if (user.role === 'manager') {
    return members.some((m) => m.user_id === user.id && m.role === 'manager')
  }
  return false
}

/**
 * Check if user can configure modules for a project
 * Only admins and super_admins can change module settings
 */
export const canConfigureModules = (user: User | null, project: Project | null): boolean => {
  if (!user || !project) return false
  if (user.role === 'super_admin') return true
  return user.role === 'admin' && user.agency_id === project.agency_id
}

/**
 * Check if user can access a specific phase
 * Combines project view permissions with phase accessibility
 */
export const canAccessPhase = (
  user: User | null,
  project: Project | null,
  _phase: number,
  members: ProjectMember[] = []
): boolean => {
  if (!user || !project) return false
  return canViewProject(user, project, members)
}
